/**
 * WorkoutRoutineForm Use Case Hook
 * Implements form state management using React Hook Form + Zod
 * Requirements: 1.1, 1.2, 4.1, 4.3, 4.6
 */

import {useState, useCallback, useRef} from 'react';
import {useForm, useFieldArray, Resolver} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {
  workoutRoutineSchema,
  createDefaultWorkoutGroup,
  generateTempId,
  isPersistedItem,
  type WorkoutRoutineFormData,
  type WorkoutGroupFormData,
  type WorkoutFormData,
  type ExerciseWorkoutFormData,
} from './WorkoutRoutineForm.schema';
import type {
  UseWorkoutRoutineFormReturn,
  FormSubmitResult,
} from './WorkoutRoutineForm.type';

/**
 * Format date to YYYY-MM-DD string
 */
const formatDate = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

/**
 * Default values for a new workout routine form
 */
const getDefaultValues = (
  initialData?: Partial<WorkoutRoutineFormData>,
  path?: 'new' | 'edit',
): WorkoutRoutineFormData => {
  const existingGroups = initialData?.workoutGroups;
  const hasWorkoutGroups = Array.isArray(existingGroups) && existingGroups.length > 0;
  const shouldCreateDefaultGroup = path === 'new' && !hasWorkoutGroups;
  
  return {
    id: initialData?.id,
    name: initialData?.name || '',
    startDate: initialData?.startDate || formatDate(new Date()),
    endDate: initialData?.endDate || formatDate(new Date()),
    cycleType: initialData?.cycleType || 'date',
    target: initialData?.target || 'programs',
    commentEnable: initialData?.commentEnable ?? true,
    showDaysOfWeek: initialData?.showDaysOfWeek ?? false,
    programIds: initialData?.programIds || [],
    programsSelected: initialData?.programsSelected || [],
    usersTargetIds: initialData?.usersTargetIds || [],
    usersTarget: initialData?.usersTarget || [],
    weekDayIds: initialData?.weekDayIds || [],
    workoutGroups: shouldCreateDefaultGroup 
      ? [createDefaultWorkoutGroup()] 
      : (initialData?.workoutGroups || []),
    isDraft: initialData?.isDraft,
    showOnlyOnScheduledDays: initialData?.showOnlyOnScheduledDays,
  };
};

export interface UseWorkoutRoutineFormOptions {
  initialData?: Partial<WorkoutRoutineFormData>;
  path?: 'new' | 'edit';
  onSubmit?: (data: WorkoutRoutineFormData) => Promise<FormSubmitResult>;
}

/**
 * Main use-case hook for WorkoutRoutineForm
 * Manages form state with React Hook Form and provides CRUD operations for nested arrays
 *
 * @param options - Configuration options for the form
 * @returns Form state and action handlers
 */
export function useWorkoutRoutineForm(
  options: UseWorkoutRoutineFormOptions = {},
): UseWorkoutRoutineFormReturn {
  const {initialData, path = 'new', onSubmit} = options;

  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  const defaultValuesRef = useRef<WorkoutRoutineFormData | null>(null);
  if (!defaultValuesRef.current) {
    defaultValuesRef.current = getDefaultValues(initialData, path);
  }

  const form = useForm<WorkoutRoutineFormData>({
    resolver: zodResolver(
      workoutRoutineSchema,
    ) as Resolver<WorkoutRoutineFormData>,
    defaultValues: defaultValuesRef.current,
    mode: 'onChange',
  });

  // Requirements: 1.2 - useFieldArray for managing dynamic collections
  const workoutGroupsField = useFieldArray({
    control: form.control,
    name: 'workoutGroups',
    keyName: 'fieldId', // Avoid conflict with 'id' field
  });

  /**
   * Add a new WorkoutGroup to the form
   * Requirements: 4.1 - append to workoutGroups array and allow immediate editing
   *
   * @param autoFocus - Whether to focus the new group's name input
   */
  const addWorkoutGroup = useCallback(
    (autoFocus = true) => {
      const newGroup = createDefaultWorkoutGroup();
      workoutGroupsField.append(newGroup);

      if (autoFocus) {
        // Focus the new group's name input after render
        setTimeout(() => {
          const newIndex = workoutGroupsField.fields.length;
          const element = document.getElementById(`workoutGroup-${newIndex}`);
          if (element) {
            element.focus();
          }
        }, 100);
      }
    },
    [workoutGroupsField],
  );

  /**
   * Remove a WorkoutGroup from the form
   * Requirements: 4.3 - mark with _destroy if persisted, or remove if new
   *
   * @param index - Index of the group to remove
   */
  const removeWorkoutGroup = useCallback(
    (index: number) => {
      const group = form.getValues(`workoutGroups.${index}`);

      if (isPersistedItem(group)) {
        // Mark persisted items with _destroy flag instead of removing
        form.setValue(`workoutGroups.${index}._destroy`, true);
      } else {
        // Remove new items directly from the array
        workoutGroupsField.remove(index);
      }
    },
    [form, workoutGroupsField],
  );

  /**
   * Move a WorkoutGroup from one position to another
   * Requirements: 4.6 - support reordering items
   *
   * @param from - Source index
   * @param to - Destination index
   */
  const moveWorkoutGroup = useCallback(
    (from: number, to: number) => {
      const groups = form.getValues('workoutGroups');

      // Validate indices
      if (
        from < 0 ||
        from >= groups.length ||
        to < 0 ||
        to >= groups.length ||
        from === to
      ) {
        return;
      }

      workoutGroupsField.move(from, to);
    },
    [form, workoutGroupsField],
  );

  /**
   * Submit the form
   * Validates data and calls the onSubmit callback
   *
   * @returns Promise with submission result
   */
  const submitForm = useCallback(async (): Promise<FormSubmitResult> => {
    setIsLoading(true);
    setProgress(0);
    setServerErrors({});

    try {
      // Trigger validation
      const isValid = await form.trigger();

      if (!isValid) {
        console.log('react-hook-form errors =======>', form.formState.errors);
        setIsLoading(false);
        return {
          success: false,
          errors: Object.fromEntries(
            Object.entries(form.formState.errors).map(([key, error]) => [
              key,
              error?.message || 'Validation error',
            ]),
          ),
        };
      }

      const data = form.getValues();
      setProgress(25);

      // Call the onSubmit callback if provided
      if (onSubmit) {
        const result = await onSubmit(data);
        setProgress(100);
        setIsLoading(false);

        if (!result.success && result.errors) {
          setServerErrors(result.errors);
        }

        return result;
      }

      setProgress(100);
      setIsLoading(false);

      return {
        success: true,
        data,
      };
    } catch (error) {
      setIsLoading(false);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      setServerErrors({form: errorMessage});

      return {
        success: false,
        errors: {form: errorMessage},
      };
    }
  }, [form, onSubmit]);

  return {
    form,
    workoutGroupsField,
    isLoading,
    progress,
    errors: serverErrors,
    addWorkoutGroup,
    removeWorkoutGroup,
    moveWorkoutGroup,
    submitForm,
  };
}

/**
 * Pure function to add a workout group to form data
 * Used for property testing
 */
export function addWorkoutGroupToFormData(
  formData: WorkoutRoutineFormData,
): WorkoutRoutineFormData {
  const newGroup = createDefaultWorkoutGroup();
  return {
    ...formData,
    workoutGroups: [...formData.workoutGroups, newGroup],
  };
}

/**
 * Pure function to remove a workout group from form data
 * Used for property testing
 *
 * @param formData - Current form data
 * @param index - Index of the group to remove
 */
export function removeWorkoutGroupFromFormData(
  formData: WorkoutRoutineFormData,
  index: number,
): WorkoutRoutineFormData {
  const groups = [...formData.workoutGroups];
  const group = groups[index];

  if (!group) {
    return formData;
  }

  if (isPersistedItem(group)) {
    // Mark persisted items with _destroy flag
    groups[index] = {...group, _destroy: true};
  } else {
    // Remove new items directly
    groups.splice(index, 1);
  }

  return {
    ...formData,
    workoutGroups: groups,
  };
}

/**
 * Pure function to move a workout group in form data
 * Used for property testing
 *
 * @param formData - Current form data
 * @param from - Source index
 * @param to - Destination index
 */
export function moveWorkoutGroupInFormData(
  formData: WorkoutRoutineFormData,
  from: number,
  to: number,
): WorkoutRoutineFormData {
  const groups = [...formData.workoutGroups];

  // Validate indices
  if (
    from < 0 ||
    from >= groups.length ||
    to < 0 ||
    to >= groups.length ||
    from === to
  ) {
    return formData;
  }

  // Remove item from source position
  const [movedItem] = groups.splice(from, 1);

  // Insert at destination position
  groups.splice(to, 0, movedItem);

  return {
    ...formData,
    workoutGroups: groups,
  };
}

/**
 * Pure function to add a workout to a workout group
 * Used for property testing
 */
export function addWorkoutToGroupInFormData(
  formData: WorkoutRoutineFormData,
  groupIndex: number,
): WorkoutRoutineFormData {
  const groups = [...formData.workoutGroups];
  const group = groups[groupIndex];

  if (!group) {
    return formData;
  }

  const newWorkout = {
    tempId: generateTempId(),
    name: '',
    description: '',
    time_text: '',
    checkinEnabled: false,
    exerciseWorkouts: [],
  };

  groups[groupIndex] = {
    ...group,
    workouts: [...(group.workouts || []), newWorkout],
  };

  return {
    ...formData,
    workoutGroups: groups,
  };
}

/**
 * Pure function to add an exercise to a workout
 * Used for property testing
 */
export function addExerciseToWorkoutInFormData(
  formData: WorkoutRoutineFormData,
  groupIndex: number,
  workoutIndex: number,
): WorkoutRoutineFormData {
  const groups = [...formData.workoutGroups];
  const group = groups[groupIndex];

  if (!group || !group.workouts || !group.workouts[workoutIndex]) {
    return formData;
  }

  const workouts = [...group.workouts];
  const workout = workouts[workoutIndex];

  const newExercise = {
    tempId: generateTempId(),
    exerciseId: '',
    repetitions: '',
    notes: '',
    rounds: '',
    load: '',
    time: '',
    restTime: '',
  };

  workouts[workoutIndex] = {
    ...workout,
    exerciseWorkouts: [...(workout.exerciseWorkouts || []), newExercise],
  };

  groups[groupIndex] = {
    ...group,
    workouts,
  };

  return {
    ...formData,
    workoutGroups: groups,
  };
}

export default useWorkoutRoutineForm;

/**
 * Pure function to edit a field in a workout group
 * Used for property testing - Property 4: Edit Field Isolation
 *
 * @param formData - Current form data
 * @param groupIndex - Index of the group to edit
 * @param fieldName - Name of the field to edit
 * @param newValue - New value for the field
 */
export function editWorkoutGroupFieldInFormData(
  formData: WorkoutRoutineFormData,
  groupIndex: number,
  fieldName: keyof WorkoutGroupFormData,
  newValue: any,
): WorkoutRoutineFormData {
  const groups = [...formData.workoutGroups];
  const group = groups[groupIndex];

  if (!group) {
    return formData;
  }

  groups[groupIndex] = {
    ...group,
    [fieldName]: newValue,
  };

  return {
    ...formData,
    workoutGroups: groups,
  };
}

/**
 * Pure function to edit a field in a workout
 * Used for property testing - Property 4: Edit Field Isolation
 *
 * @param formData - Current form data
 * @param groupIndex - Index of the parent group
 * @param workoutIndex - Index of the workout to edit
 * @param fieldName - Name of the field to edit
 * @param newValue - New value for the field
 */
export function editWorkoutFieldInFormData(
  formData: WorkoutRoutineFormData,
  groupIndex: number,
  workoutIndex: number,
  fieldName: keyof WorkoutFormData,
  newValue: any,
): WorkoutRoutineFormData {
  const groups = [...formData.workoutGroups];
  const group = groups[groupIndex];

  if (!group || !group.workouts || !group.workouts[workoutIndex]) {
    return formData;
  }

  const workouts = [...group.workouts];
  const workout = workouts[workoutIndex];

  workouts[workoutIndex] = {
    ...workout,
    [fieldName]: newValue,
  };

  groups[groupIndex] = {
    ...group,
    workouts,
  };

  return {
    ...formData,
    workoutGroups: groups,
  };
}

/**
 * Pure function to edit a field in an exercise workout
 * Used for property testing - Property 4: Edit Field Isolation
 *
 * @param formData - Current form data
 * @param groupIndex - Index of the parent group
 * @param workoutIndex - Index of the parent workout
 * @param exerciseIndex - Index of the exercise to edit
 * @param fieldName - Name of the field to edit
 * @param newValue - New value for the field
 */
export function editExerciseFieldInFormData(
  formData: WorkoutRoutineFormData,
  groupIndex: number,
  workoutIndex: number,
  exerciseIndex: number,
  fieldName: keyof ExerciseWorkoutFormData,
  newValue: any,
): WorkoutRoutineFormData {
  const groups = [...formData.workoutGroups];
  const group = groups[groupIndex];

  if (!group || !group.workouts || !group.workouts[workoutIndex]) {
    return formData;
  }

  const workouts = [...group.workouts];
  const workout = workouts[workoutIndex];

  if (!workout.exerciseWorkouts || !workout.exerciseWorkouts[exerciseIndex]) {
    return formData;
  }

  const exercises = [...workout.exerciseWorkouts];
  const exercise = exercises[exerciseIndex];

  exercises[exerciseIndex] = {
    ...exercise,
    [fieldName]: newValue,
  };

  workouts[workoutIndex] = {
    ...workout,
    exerciseWorkouts: exercises,
  };

  groups[groupIndex] = {
    ...group,
    workouts,
  };

  return {
    ...formData,
    workoutGroups: groups,
  };
}
